const DAY_MS = 24 * 60 * 60 * 1000;

const pluralize = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const THEME_DEFINITIONS = [
  {
    id: 'onboarding-clarity',
    label: 'Onboarding clarity',
    description: 'Signals that dads are confused about where to start or what comes next.',
    keywords: ['onboarding', 'start here', 'getting started', 'where to start', 'confusing', 'confused', 'unclear'],
    suggestedAction: 'Review onboarding and Start Here copy',
    route: 'In Dad Partners',
  },
  {
    id: 'practical-guidance',
    label: 'Practical guidance',
    description: 'Dads want more concrete help, examples, or next-step guidance.',
    keywords: ['practical', 'specific', 'actionable', 'how to', 'example', 'examples', 'guidance', 'tips'],
    suggestedAction: 'Review prompts, Gems, or recordings for more concrete help',
    route: 'In Dad Partners',
  },
  {
    id: 'scheduling-friction',
    label: 'Scheduling friction',
    description: 'Timing, calendar, and weekly rhythm are getting in the way.',
    keywords: ['schedule', 'scheduling', 'calendar', 'time', 'timing', 'sunday', 'zoom', 'busy'],
    suggestedAction: 'Review event timing and weekly rhythm friction',
    route: 'In Dad Partners',
  },
  {
    id: 'community-support',
    label: 'Community support',
    description: 'Signals about belonging, encouragement, and how supported dads feel.',
    keywords: ['community', 'supported', 'support', 'encouraged', 'encouragement', 'connected', 'connection', 'brothers', 'not alone'],
    suggestedAction: 'Reinforce the support touchpoints that are landing well',
    route: 'In Dad Partners',
  },
  {
    id: 'content-fit',
    label: 'Content fit',
    description: 'Recordings, prompts, Gems, or resources are being named directly.',
    keywords: ['recording', 'recordings', 'prompt', 'prompts', 'gem', 'gems', 'resource', 'resources', 'video', 'content'],
    suggestedAction: 'Review which support assets need to be refreshed next',
    route: 'In Dad Partners',
  },
  {
    id: 'guide-partner-signal',
    label: 'Guide Partner signal',
    description: 'Feedback is pointing toward guide stories, examples, or story-sharing needs.',
    keywords: ['guide partner', 'guest guide', 'story', 'stories', 'speaker', 'share their story'],
    suggestedAction: 'Bring this signal into Guide Partners',
    route: 'In Guide Partners',
  },
];

const EMOTIONAL_SIGNAL_RULES = [
  {
    reason: 'sounds isolated',
    tone: 'attention',
    keywords: ['alone', 'isolated', 'lonely', 'disconnected', 'on my own'],
  },
  {
    reason: 'sounds burdened',
    tone: 'attention',
    keywords: ['overwhelmed', 'burdened', 'heavy', 'exhausted', 'drained', 'burned out', 'tired'],
  },
  {
    reason: 'sounds discouraged',
    tone: 'attention',
    keywords: ['discouraged', 'stuck', 'struggling', 'hard', 'hurting', 'hopeless', 'frustrated'],
  },
  {
    reason: 'sounds confused',
    tone: 'next',
    keywords: ['confused', 'unclear', 'lost', 'not sure', 'don’t know where to start', "don't know where to start"],
  },
];

const getTimeValue = (value) => {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeText = (value) => (value || '').toString().toLowerCase();

const clipText = (value, limit = 150) => {
  const text = (value || '').trim();
  if (text.length <= limit) return text;
  return `${text.slice(0, limit - 1).trimEnd()}…`;
};

const formatRelativeDate = (value) => {
  const timestamp = getTimeValue(value);
  if (!timestamp) return 'Date unknown';

  const diff = Date.now() - timestamp;
  if (diff < DAY_MS) return 'Today';
  if (diff < DAY_MS * 2) return '1 day ago';
  const days = Math.max(1, Math.round(diff / DAY_MS));
  return `${days} days ago`;
};

const formatFeedbackTone = (item) => {
  if (item?.sentiment === 'negative') return 'Negative signal';
  if (item?.sentiment === 'mixed') return 'Mixed signal';
  if (item?.sentiment === 'positive') return 'Positive signal';
  return 'Feedback signal';
};

const buildFeedbackSearchText = (item = {}) => normalizeText([
  item.feedback,
  item.category,
  item.member_name,
  item.member,
].filter(Boolean).join(' '));

const buildAtRiskLookup = (atRiskMembers = []) => {
  const lookup = new Set();
  atRiskMembers.forEach((member) => {
    const name = normalizeText(member?.name);
    const email = normalizeText(member?.email);
    if (name) lookup.add(`name:${name}`);
    if (email) lookup.add(`email:${email}`);
  });
  return lookup;
};

const isAtRiskFeedback = (item, atRiskLookup) => {
  const name = normalizeText(item?.member_name || item?.member);
  const email = normalizeText(item?.member_email || item?.email);
  return (name && atRiskLookup.has(`name:${name}`)) || (email && atRiskLookup.has(`email:${email}`));
};

function scoreUrgency(item, atRiskLookup) {
  const searchText = buildFeedbackSearchText(item);
  const hits = [];
  let score = 0;

  if (item?.sentiment === 'negative') {
    score += 5;
    hits.push({ reason: 'negative sentiment', tone: 'attention' });
  } else if (item?.sentiment === 'mixed') {
    score += 3;
    hits.push({ reason: 'mixed sentiment', tone: 'next' });
  }

  const rating = Number(item?.rating || 0);
  if (rating > 0 && rating <= 2) {
    score += 4;
    hits.push({ reason: 'low rating', tone: 'attention' });
  } else if (rating === 3) {
    score += 2;
  }

  EMOTIONAL_SIGNAL_RULES.forEach((rule) => {
    if (rule.keywords.some((keyword) => searchText.includes(keyword))) {
      score += rule.tone === 'attention' ? 4 : 2;
      hits.push({ reason: rule.reason, tone: rule.tone });
    }
  });

  if (isAtRiskFeedback(item, atRiskLookup)) {
    score += 3;
    hits.push({ reason: 'connected to an at-risk dad', tone: 'attention' });
  }

  const createdAt = getTimeValue(item?.created_at || item?.date);
  if (createdAt > 0) {
    const ageDays = Math.floor((Date.now() - createdAt) / DAY_MS);
    score += Math.max(0, 2 - Math.min(ageDays, 2));
  }

  return {
    score,
    hits,
  };
}

function deriveUrgentItems(feedbackItems, atRiskLookup) {
  return feedbackItems
    .map((item) => {
      const urgency = scoreUrgency(item, atRiskLookup);
      const primaryHit = urgency.hits[0];
      const tone = primaryHit?.tone || (item?.sentiment === 'negative' ? 'attention' : 'next');

      return {
        id: item.feedback_id || item.id || `${item.member_name || 'feedback'}-${item.created_at || item.date || Math.random()}`,
        score: urgency.score,
        tone,
        title: item.member_name || item.member || 'Anonymous dad',
        excerpt: clipText(item.feedback, 160),
        detail: `${primaryHit?.reason || formatFeedbackTone(item)} · ${formatRelativeDate(item.created_at || item.date)}`,
        actionLabel: primaryHit?.tone === 'attention' ? 'Follow up personally' : 'Read in full',
        location: 'In Feedback',
      };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 4);
}

function derivePatterns(feedbackItems) {
  const groups = THEME_DEFINITIONS.map((theme) => ({ ...theme, matches: [] }));

  feedbackItems.forEach((item) => {
    const searchText = buildFeedbackSearchText(item);
    groups.forEach((group) => {
      if (group.keywords.some((keyword) => searchText.includes(keyword))) {
        group.matches.push(item);
      }
    });
  });

  return groups
    .filter((group) => group.matches.length > 0)
    .map((group) => {
      const negativeCount = group.matches.filter((item) => item.sentiment === 'negative').length;
      const positiveCount = group.matches.filter((item) => item.sentiment === 'positive').length;
      const tone = negativeCount > 0 ? 'attention' : positiveCount === group.matches.length ? 'steady' : 'next';

      return {
        id: group.id,
        label: group.label,
        count: group.matches.length,
        description: group.description,
        tone,
        route: group.route,
        suggestedAction: group.suggestedAction,
        sampleExcerpts: group.matches
          .slice(0, 2)
          .map((item) => clipText(item.feedback, 110)),
      };
    })
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      const tonePriority = { attention: 2, next: 1, steady: 0 };
      return (tonePriority[b.tone] || 0) - (tonePriority[a.tone] || 0);
    })
    .slice(0, 3);
}

function deriveSuggestedActions({ urgentItems, patterns, aiInsights, atRiskMembers = [] }) {
  const actions = [];

  if (urgentItems.length > 0) {
    const attentionCount = urgentItems.filter((item) => item.tone === 'attention').length;
    actions.push({
      tone: attentionCount > 0 ? 'attention' : 'next',
      eyebrow: 'Follow up',
      title: `Follow up personally with ${pluralize(Math.max(1, attentionCount || urgentItems.length), 'dad')}`,
      detail: urgentItems.slice(0, 2).map((item) => item.title).join(', '),
      location: 'From Catch Pain Early',
    });
  }

  patterns.forEach((pattern) => {
    if (actions.length >= 3) return;
    if (pattern.count < 2 && pattern.tone !== 'attention') return;

    actions.push({
      tone: pattern.tone,
      eyebrow: 'Suggested action',
      title: pattern.suggestedAction,
      detail: `${pluralize(pattern.count, 'feedback item')} are pointing in this direction.`,
      location: pattern.route,
    });
  });

  const topObjective = aiInsights?.objectives?.[0];
  if (topObjective && actions.length < 3) {
    actions.push({
      tone: topObjective.priority === 'High' ? 'attention' : 'next',
      eyebrow: 'AI synthesis',
      title: topObjective.objective,
      detail: topObjective.action,
      location: 'From Feedback analysis',
    });
  }

  if (!actions.length && atRiskMembers.length > 0) {
    actions.push({
      tone: 'attention',
      eyebrow: 'Watch closely',
      title: `Keep listening around ${pluralize(atRiskMembers.length, 'at-risk dad')}`,
      detail: 'No strong feedback pattern is stacked yet, but retention pressure is present.',
      location: 'Across Analytics and Feedback',
    });
  }

  if (!actions.length) {
    actions.push({
      tone: 'steady',
      eyebrow: 'Quiet',
      title: 'Review the newest feedback and wait for the next real pattern',
      detail: 'Nothing is loud enough yet to justify a bigger move.',
      location: 'In Feedback',
    });
  }

  return actions.slice(0, 3);
}

export function buildListenLearnRoomContext({ room, feedbackItems = [], atRiskMembers = [], aiInsights = null, primaryAction, secondaryAction }) {
  const atRiskLookup = buildAtRiskLookup(atRiskMembers);
  const urgentItems = deriveUrgentItems(feedbackItems, atRiskLookup);
  const patterns = derivePatterns(feedbackItems);
  const suggestedActions = deriveSuggestedActions({
    urgentItems,
    patterns,
    aiInsights,
    atRiskMembers,
  });

  const feedbackCount = feedbackItems.length;
  const difficultFeedbackCount = feedbackItems.filter(
    (item) => item.sentiment === 'negative' || item.sentiment === 'mixed'
  ).length;
  const statusTone = urgentItems.length > 0 ? 'attention' : patterns.length > 0 ? 'next' : 'steady';
  const statusLabel = urgentItems.length > 0 ? 'Catch pain early' : patterns.length > 0 ? 'Patterns in view' : 'Quiet';

  return {
    id: room.id,
    variant: 'listen-learn',
    statusTone,
    statusLabel,
    startTitle: urgentItems.length > 0
      ? 'Listen to the hard signals first, then step back and read the pattern'
      : patterns.length > 0
        ? 'A few themes are beginning to repeat'
        : 'Keep listening for the next real pattern',
    startDetail: urgentItems.length > 0
      ? 'This room should surface pain before it gets buried, then help you decide what deserves a follow-up, a change, or a closer watch.'
      : 'This room turns feedback into discernment by showing what feels urgent, what is repeating, and what may deserve a next move.',
    signals: [
      { tone: feedbackCount > 0 ? 'next' : 'steady', text: `${pluralize(feedbackCount, 'feedback item')} in recent view` },
      { tone: difficultFeedbackCount > 0 ? 'attention' : 'steady', text: difficultFeedbackCount > 0 ? `${pluralize(difficultFeedbackCount, 'mixed or negative signal')} surfaced` : 'No difficult signals are stacked up right now.' },
      { tone: atRiskMembers.length > 0 ? 'attention' : 'steady', text: atRiskMembers.length > 0 ? `${pluralize(atRiskMembers.length, 'at-risk dad')} can add weight to what you are hearing` : 'No at-risk dad cues are shaping this room right now.' },
    ],
    metrics: [
      { label: 'Feedback items', value: feedbackCount, detail: 'Recent voices in view', tone: feedbackCount > 0 ? 'next' : 'steady' },
      { label: 'Catch pain early', value: urgentItems.length, detail: 'Items worth human attention first', tone: urgentItems.length > 0 ? 'attention' : 'steady' },
      { label: 'Patterns in view', value: patterns.length, detail: 'Repeated themes worth discerning', tone: patterns.length > 0 ? 'next' : 'steady' },
    ],
    primaryAction,
    secondaryAction,
    urgentItems,
    patterns,
    suggestedActions,
    analysisSummary: aiInsights?.summary || '',
  };
}
